// src/pages/service-orders/index.jsx
// Vista del TÉCNICO: gestión de instrumentos de una Orden de Compra asignada.
// Permite cambiar estados individualmente o en bloque, cargar notas y rechazos.
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../utils/supabase";
import { useLabPermissions } from "../../hooks/useLabPermissions";
import {
  hasCurrentAdmissionTransition,
  resolveAdmissionReceptionDate,
  resolveAdmissionStatus,
} from "../../utils/instrument-admission";
import { canSetStatus } from "../../utils/statusPermissions";
import { resolveCalibrationRoute, resolveCalibrationFamily, calibrationFamilyLabel } from "../../utils/calibration-routing";
import { equipmentTypeLabel, resolveOperationsReturnPath } from "../../utils/operations-view";
import { useAuth } from "../../components/system/AppGate";
import ExternalCertUploadModal from "../../components/ExternalCertUploadModal";
import ServiceOrderConversation from "../../components/ServiceOrderConversation";
import { fetchServiceOrderMessageSummaries } from "../../utils/service-order-messages";

// ─── helpers ────────────────────────────────────────────────────────────────

function formatDateEU(dateStr) {
  if (!dateStr) return "—";
  const [y, m, d] = String(dateStr).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

const STATUS_CONFIG = {
  pending_reception: { label: "Pendiente de recepción", cls: "bg-gray-500/15 border-gray-500/40 text-gray-300" },
  active:            { label: "En depósito",         cls: "bg-slate-500/15 border-slate-500/40 text-slate-200" },
  in_progress:       { label: "En proceso",          cls: "bg-amber-500/15 border-amber-500/40 text-amber-200" },
  paused:            { label: "Pausado",             cls: "bg-yellow-500/15 border-yellow-500/40 text-yellow-200" },
  calibrated:        { label: "Calibrado",           cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200" },
  rejected:          { label: "Rechazado",           cls: "bg-rose-500/15 border-rose-500/40 text-rose-200" },
  with_certificate:  { label: "Con certificado",     cls: "bg-cyan-500/15 border-cyan-500/40 text-cyan-200" },
  without_certificate: { label: "Sin certificado",   cls: "bg-zinc-500/15 border-zinc-500/40 text-zinc-200" },
  reviewed:          { label: "Revisado por jefe",   cls: "bg-violet-500/15 border-violet-500/40 text-violet-200" },
  ready_to_remit:    { label: "Listo para remitar",  cls: "bg-sky-500/15 border-sky-500/40 text-sky-200" },
  remitted:          { label: "Remitido",cls: "bg-teal-500/15 border-teal-500/40 text-teal-200" },
  delivered:         { label: "Entregado",           cls: "bg-indigo-500/15 border-indigo-500/40 text-indigo-200" },
  delivered_with_certificate:    { label: "Entregado con certificado",  cls: "bg-indigo-500/15 border-indigo-500/40 text-indigo-200" },
  delivered_without_certificate: { label: "Entregado sin certificado",  cls: "bg-orange-500/15 border-orange-500/40 text-orange-200" },
  delivered_not_calibrated:      { label: "Entregado no calibrado",     cls: "bg-orange-500/15 border-orange-500/40 text-orange-200" },
  delivered_not_repaired:        { label: "Entregado no reparado",      cls: "bg-orange-500/15 border-orange-500/40 text-orange-200" },
  delivered_repaired:            { label: "Entregado reparado",         cls: "bg-indigo-500/15 border-indigo-500/40 text-indigo-200" },
  cancelled:         { label: "Cancelado",           cls: "bg-zinc-600/15 border-zinc-600/40 text-zinc-300" },
};

// Opciones de estado para la acción masiva según rol
const BULK_OPTIONS_TECH = [
  { value: "in_progress", label: "▶ En proceso" },
  { value: "calibrated",  label: "✓ Calibrado" },
];
const BULK_OPTIONS_MANAGER = [
  { value: "in_progress",      label: "▶ En proceso" },
  { value: "calibrated",       label: "✓ Calibrado" },
  { value: "reviewed",         label: "📄 Revisado por jefe" },
  { value: "ready_to_remit",   label: "📤 Listo para remitar" },
];

// Color de acento (borde izquierdo de la tarjeta) para escanear de un vistazo
const STATUS_ACCENT = {
  pending_reception: "border-l-gray-400",
  active:            "border-l-slate-400",
  in_progress:       "border-l-amber-400",
  paused:            "border-l-yellow-400",
  calibrated:        "border-l-emerald-400",
  rejected:          "border-l-rose-500",
  with_certificate:  "border-l-cyan-400",
  without_certificate: "border-l-zinc-400",
  reviewed:          "border-l-violet-400",
  ready_to_remit:    "border-l-sky-400",
  remitted:          "border-l-teal-400",
  delivered:         "border-l-indigo-400",
};
const statusAccent = (s) => STATUS_ACCENT[s] || "border-l-white/20";

function StatusBadge({ status, big = false }) {
  const cfg = STATUS_CONFIG[status] || { label: status || "—", cls: "bg-white/10 border-white/20 text-white" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${cfg.cls} ${big ? "px-3 py-1 text-sm" : "px-2.5 py-0.5 text-xs"}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
      {cfg.label}
    </span>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-xl border px-4 py-3 shadow-xl text-sm
      ${type === "error"
        ? "bg-rose-950/90 border-rose-700 text-rose-100"
        : "bg-emerald-950/90 border-emerald-700 text-emerald-100"}`}>
      <span>{message}</span>
      <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
    </div>
  );
}

// ─── modal de rechazo ────────────────────────────────────────────────────────

function RejectionModal({ instrument, onSave, onCancel, saving }) {
  const [reason, setReason] = useState(instrument?.rejection_reason || "");
  const [isBroken, setIsBroken] = useState(instrument?.is_broken || false);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-rose-500/30 bg-[#0F1115] p-5 space-y-4">
        <h3 className="text-base font-semibold text-rose-300">Rechazar instrumento</h3>
        <p className="text-xs text-white/50">
          {instrument?.instrument_brand} {instrument?.instrument_model}
          {instrument?.instrument_tag ? ` · Tag: ${instrument.instrument_tag}` : ""}
        </p>

        <div className="space-y-1">
          <label className="text-xs text-white/60 uppercase tracking-wide">Motivo del rechazo *</label>
          <textarea
            className="w-full min-h-[90px] rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-rose-500/50"
            placeholder="Ej: Fuera de tolerancia en rango 0-100 bar, aguja trabada…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <label className="flex items-center gap-3 cursor-pointer group">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-white/20 bg-white/5 accent-rose-500"
            checked={isBroken}
            onChange={(e) => setIsBroken(e.target.checked)}
          />
          <span className="text-sm text-white/80 group-hover:text-white">
            Instrumento roto o con daño físico
          </span>
        </label>

        {isBroken && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
            El gerente deberá consultar al cliente si desea enviarlo a reparación.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 hover:bg-white/5"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!reason.trim() || saving}
            onClick={() => onSave({ reason: reason.trim(), isBroken })}
            className="rounded-lg border border-rose-500/50 bg-rose-500/10 px-4 py-2 text-sm text-rose-200 hover:bg-rose-500/20 disabled:opacity-40"
          >
            {saving ? "Guardando…" : "Confirmar rechazo"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── fila de instrumento ─────────────────────────────────────────────────────

function InstrumentRow({ inst, onUpdateStatus, role, userId, selected, onToggleSelect }) {
  const [savingStatus, setSavingStatus] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadedMsg, setUploadedMsg] = useState(null);

  const status = inst.instrument_status || "active";

  // Permisos por estado según la matriz (statusPermissions.js).
  const canStart     = canSetStatus(role, "in_progress");      // técnico + jefe
  const canCalibrate = canSetStatus(role, "calibrated");       // técnico + jefe
  const canCert      = canSetStatus(role, "with_certificate"); // técnico + jefe
  const canPause     = canSetStatus(role, "paused");           // solo jefe
  const canReject    = canSetStatus(role, "rejected");         // solo jefe
  const canReview    = canSetStatus(role, "reviewed");         // solo jefe

  // Qué botón corresponde según el estado actual del instrumento.
  const showStart  = canStart && (status === "active" || status === "paused");
  const showCal    = canCalibrate && ["active", "in_progress", "paused"].includes(status);
  const showCert   = canCert && ["calibrated", "with_certificate", "without_certificate"].includes(status);
  const showPause  = canPause && status === "in_progress";
  const showReject = canReject && ["active", "in_progress", "paused", "calibrated"].includes(status);
  const showReview = canReview && ["calibrated", "with_certificate"].includes(status);
  const anyAction  = showStart || showCal || showCert || showPause || showReject || showReview;

  // Ruta a la pantalla de calibración según la magnitud del instrumento.
  const navigate = useNavigate();
  const calInstrument = { id: inst.instrument_id, type: inst.instrument_type, unit: inst.instrument_unit };
  const calRoute = resolveCalibrationRoute(calInstrument, { serviceOrderCode: inst.code });
  const calFamily = resolveCalibrationFamily(calInstrument);
  // Mostrar "Calibrar" a quien calibra (técnico/jefe), en estados de trabajo.
  const showCalibrar = canCalibrate && !!calRoute &&
    ["active", "in_progress", "paused", "calibrated", "with_certificate", "without_certificate"].includes(status);
  // Subir certificado externo (instrumentos especiales): a quien calibra, en
  // estados de trabajo. Se muestra AUNQUE no haya pantalla de calibración
  // (esos son justamente los instrumentos que lo necesitan).
  const showUpload = canCalibrate &&
    ["active", "in_progress", "paused", "calibrated", "with_certificate", "without_certificate"].includes(status);

  // Deshacer: volver al estado ANTERIOR (p.ej. apretó "Calibrado" por error).
  // Gateado por la matriz: solo se muestra si el rol puede setear ese estado
  // previo — así el técnico vuelve de Calibrado→En proceso, pero no toca los
  // estados de la oficina.
  const REVERT_TO = {
    in_progress: "active",
    calibrated: "in_progress",
    with_certificate: "calibrated",
    without_certificate: "calibrated",
    reviewed: "calibrated",
    rejected: "in_progress",
  };
  const revertTarget = REVERT_TO[status] || null;
  const showRevert = !!revertTarget && canSetStatus(role, revertTarget);

  // Selección masiva: no sobre estados de cierre
  const isSelectable = !["remitted", "delivered", "delivered_with_certificate",
    "delivered_without_certificate", "delivered_not_calibrated", "delivered_not_repaired",
    "delivered_repaired", "cancelled"].includes(status);

  async function handleStatus(newStatus, extra = {}) {
    if (savingStatus) return;
    setSavingStatus(true);
    await onUpdateStatus(inst.instrument_id, newStatus, extra);
    setSavingStatus(false);
  }

  async function handleRejectSave({ reason, isBroken }) {
    setSavingStatus(true);
    await onUpdateStatus(inst.instrument_id, "rejected", {
      rejection_reason: reason,
      is_broken: isBroken,
    });
    setSavingStatus(false);
    setShowRejectModal(false);
  }

  return (
    <>
      {showRejectModal && (
        <RejectionModal
          instrument={inst}
          onSave={handleRejectSave}
          onCancel={() => setShowRejectModal(false)}
          saving={savingStatus}
        />
      )}

      <ExternalCertUploadModal
        open={uploadOpen}
        instrument={{
          id: inst.instrument_id,
          lab_id: inst.instrument_lab_id ?? inst.lab_id,
          client_id: inst.client_id,
          tag: inst.instrument_tag,
        }}
        onClose={() => setUploadOpen(false)}
        onUploaded={(cert) => setUploadedMsg(cert?.certificate_number || '')}
      />

      <div className={`rounded-xl border-y border-r border-l-4 border-y-white/10 border-r-white/10 ${statusAccent(status)} p-3.5 transition-colors ${
        selected ? "bg-sky-500/10 ring-2 ring-sky-500/40" : "bg-white/[0.03]"
      }`}>
        {/* Encabezado del instrumento */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-start gap-3">
            {isSelectable && (
              <label className="mt-1 flex-shrink-0 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggleSelect(inst.instrument_id)}
                  className="h-4 w-4 rounded border-white/20 bg-white/5 accent-sky-500"
                />
              </label>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-mono text-white/35">{inst.code}</span>
                <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-xs font-medium text-sky-200">
                  Tipo: {equipmentTypeLabel(inst)}
                </span>
                <span className="text-[15px] font-semibold text-white">
                  {inst.instrument_brand} {inst.instrument_model}
                </span>
                {inst.instrument_tag && inst.instrument_tag !== "-" && (
                  <span className="text-xs text-white/50">Tag: {inst.instrument_tag}</span>
                )}
              </div>
              <div className="mt-0.5 text-xs text-white/40">
                Serie: {inst.instrument_serial || "—"}
                {inst.instrument_type ? ` · Magnitud: ${equipmentTypeLabel({ instrument_type: inst.instrument_type })}` : ""}
                {inst.reception_date ? ` · Ingresó: ${formatDateEU(inst.reception_date)}` : ""}
              </div>
            </div>
          </div>
          <StatusBadge status={status} big />
        </div>

        {/* Motivo de rechazo */}
        {status === "rejected" && inst.rejection_reason && (
          <div className="mt-3 rounded-lg border border-rose-500/20 bg-rose-500/5 px-3 py-2 text-xs text-rose-200 space-y-1">
            <div className="font-medium uppercase tracking-wide text-rose-300/60">Motivo de rechazo</div>
            <div>{inst.rejection_reason}</div>
            {inst.is_broken && (
              <div className="text-amber-300">⚠ Instrumento roto — pendiente consulta con cliente</div>
            )}
          </div>
        )}

        {/* Abrir la pantalla de calibración (cargar mediciones y certificado) */}
        {showCalibrar && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => navigate(calRoute)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500/60 bg-sky-500/20 px-3.5 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/30 transition-colors"
            >
              🔬 Calibrar · cargar mediciones ({calibrationFamilyLabel(calFamily)})
            </button>
          </div>
        )}

        {/* Instrumentos especiales: subir un certificado hecho por fuera de la app */}
        {showUpload && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              title="Instrumentos especiales: subir un certificado hecho por fuera de la app (PDF). Lo aprueba el jefe."
              className="inline-flex items-center gap-1.5 rounded-lg border border-violet-500/50 bg-violet-500/15 px-3.5 py-2 text-sm font-medium text-violet-100 hover:bg-violet-500/25 transition-colors"
            >
              📎 Subir certificado (especial)
            </button>
            {uploadedMsg && (
              <div className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200">
                Certificado <b>{uploadedMsg}</b> subido. Queda <b>pendiente de aprobación</b> del jefe.
              </div>
            )}
          </div>
        )}

        {/* Acciones de laboratorio (técnico y jefe, según rol y estado) */}
        {(anyAction || showRevert) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {showStart && (
              <button
                type="button"
                disabled={savingStatus}
                onClick={() => handleStatus("in_progress")}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/50 bg-amber-500/15 px-3.5 py-2 text-sm font-medium text-amber-100 hover:bg-amber-500/25 transition-colors disabled:opacity-40"
              >
                ▶ {status === "paused" ? "Reanudar" : "Comenzar"}
              </button>
            )}
            {showCal && (
              <button
                type="button"
                disabled={savingStatus}
                onClick={() => handleStatus("calibrated")}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3.5 py-2 text-sm font-medium text-emerald-100 hover:bg-emerald-500/25 transition-colors disabled:opacity-40"
              >
                ✓ Calibrado
              </button>
            )}
            {showCert && (
              <>
                <button
                  type="button"
                  disabled={savingStatus}
                  onClick={() => handleStatus("with_certificate")}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-500/50 bg-cyan-500/15 px-3.5 py-2 text-sm font-medium text-cyan-100 hover:bg-cyan-500/25 transition-colors disabled:opacity-40"
                >
                  📄 Con certificado
                </button>
                <button
                  type="button"
                  disabled={savingStatus}
                  onClick={() => handleStatus("without_certificate")}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-400/50 bg-zinc-400/15 px-3.5 py-2 text-sm font-medium text-zinc-100 hover:bg-zinc-400/25 transition-colors disabled:opacity-40"
                >
                  🚫 Sin certificado
                </button>
              </>
            )}
            {showPause && (
              <button
                type="button"
                disabled={savingStatus}
                onClick={() => handleStatus("paused")}
                className="inline-flex items-center gap-1.5 rounded-lg border border-yellow-500/50 bg-yellow-500/15 px-3.5 py-2 text-sm font-medium text-yellow-100 hover:bg-yellow-500/25 transition-colors disabled:opacity-40"
              >
                ⏸ Pausar
              </button>
            )}
            {showReject && (
              <button
                type="button"
                disabled={savingStatus}
                onClick={() => setShowRejectModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-500/50 bg-rose-500/15 px-3.5 py-2 text-sm font-medium text-rose-100 hover:bg-rose-500/25 transition-colors disabled:opacity-40"
              >
                ✕ Rechazar
              </button>
            )}
            {showReview && (
              <button
                type="button"
                disabled={savingStatus}
                onClick={() => handleStatus("reviewed", {
                  reviewed_by: userId || null,
                  reviewed_at: new Date().toISOString(),
                })}
                className="inline-flex items-center gap-1.5 rounded-lg border border-violet-500/50 bg-violet-500/15 px-3.5 py-2 text-sm font-medium text-violet-100 hover:bg-violet-500/25 transition-colors disabled:opacity-40"
              >
                📄 Revisar certificado
              </button>
            )}
            {showRevert && (
              <button
                type="button"
                disabled={savingStatus}
                onClick={() => {
                  if (confirm(`¿Volver a "${STATUS_CONFIG[revertTarget]?.label || revertTarget}"? El instrumento vuelve al estado anterior.`)) {
                    handleStatus(revertTarget);
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/5 px-3.5 py-2 text-sm font-medium text-slate-300 hover:bg-white/10 transition-colors disabled:opacity-40"
                title="Deshacer: volver al estado anterior"
              >
                ↩ Volver a {STATUS_CONFIG[revertTarget]?.label || revertTarget}
              </button>
            )}
          </div>
        )}

        {/* Revisión registrada */}
        {inst.reviewed_at && (
          <div className="mt-3 rounded-lg border border-violet-500/20 bg-violet-500/5 px-3 py-2 text-xs text-violet-200">
            Certificado revisado el {formatDateEU(inst.reviewed_at?.slice(0, 10))}
          </div>
        )}

        <ServiceOrderConversation
          className="mt-3"
          serviceOrderId={inst.service_order_id}
          legacyNote={inst.technician_notes}
          initialSummary={inst.message_summary}
        />
      </div>
    </>
  );
}

// ─── barra de acción masiva ──────────────────────────────────────────────────

// Candidatos para la acción masiva; se filtran por rol con la matriz.
// Se excluye "Rechazar" (necesita motivo) y "Listo para remitar" (automático).
const BULK_CANDIDATES = [
  { value: "in_progress",         label: "▶ En proceso" },
  { value: "calibrated",          label: "✓ Calibrado" },
  { value: "with_certificate",    label: "📄 Con certificado" },
  { value: "without_certificate", label: "🚫 Sin certificado" },
  { value: "reviewed",            label: "📄 Revisado por jefe" },
];

function BulkActionBar({ count, role, onApply, onClear, applying }) {
  const options = BULK_CANDIDATES.filter((o) => canSetStatus(role, o.value));
  const [bulkStatus, setBulkStatus] = useState(options[0]?.value || "");

  return (
    <div className="sticky top-[57px] z-10 rounded-xl border border-sky-500/40 bg-sky-950/80 backdrop-blur px-4 py-3 flex flex-wrap items-center gap-3 shadow-lg shadow-black/40">
      <div className="flex items-center gap-2">
        <span className="h-5 w-5 rounded-full bg-sky-500 text-[11px] font-bold text-white flex items-center justify-center">
          {count}
        </span>
        <span className="text-sm text-sky-200 font-medium">
          {count === 1 ? "instrumento seleccionado" : "instrumentos seleccionados"}
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs text-sky-300/70">Cambiar a:</label>
        <select
          value={bulkStatus}
          onChange={(e) => setBulkStatus(e.target.value)}
          disabled={applying}
          className="rounded-lg border border-sky-500/40 bg-sky-900/50 px-2.5 py-1.5 text-xs text-sky-100 focus:outline-none focus:border-sky-400/60 disabled:opacity-50"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <button
          type="button"
          disabled={applying || !bulkStatus}
          onClick={() => bulkStatus && onApply(bulkStatus)}
          className="rounded-lg border border-sky-500/60 bg-sky-500/20 px-3 py-1.5 text-xs text-sky-100 hover:bg-sky-500/30 disabled:opacity-40 font-medium"
        >
          {applying ? "Aplicando…" : "Aplicar a seleccionados"}
        </button>

        <button
          type="button"
          onClick={onClear}
          className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-white/50 hover:text-white/80 hover:border-white/20"
        >
          Limpiar selección
        </button>
      </div>
    </div>
  );
}

// ─── resumen de estados ──────────────────────────────────────────────────────

function StatusSummary({ instruments }) {
  const counts = {};
  for (const inst of instruments) {
    const s = inst.instrument_status || "active";
    counts[s] = (counts[s] || 0) + 1;
  }

  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([status, n]) => {
        const cfg = STATUS_CONFIG[status] || { label: status, cls: "bg-white/10 border-white/20 text-white" };
        return (
          <span key={status} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs ${cfg.cls}`}>
            <span className="font-bold">{n}</span>
            <span className="opacity-80">{cfg.label}</span>
          </span>
        );
      })}
    </div>
  );
}

// ─── página principal ─────────────────────────────────────────────────────────

export default function ServiceOrderInstrumentsPage() {
  const { id: poId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const p = useLabPermissions();
  const { user } = useAuth();
  const returnTo = resolveOperationsReturnPath(location.state?.returnTo);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [headerInfo, setHeaderInfo] = useState(null);
  const [instruments, setInstruments] = useState([]);
  const [toast, setToast] = useState(null);

  // Selección para acción masiva
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [applying, setApplying] = useState(false);

  // Protección de acceso
  useEffect(() => {
    if (p.loading) return;
    if (!p.isTechnician && !p.isLabManager && !p.isAdmin) {
      navigate("/login", { replace: true });
    }
  }, [p, navigate]);

  const load = useCallback(async () => {
    if (!poId) return;
    try {
      setErrorMsg("");

      const { data: poRow, error: poErr } = await supabase
        .from("v_purchase_orders")
        .select("id, code, client_name, ops_priority, ops_status, ops_due_date, assigned_to")
        .eq("id", poId)
        .maybeSingle();
      if (poErr) throw poErr;
      if (!poRow) throw new Error("No se encontró la orden de servicio.");

      setHeaderInfo({
        poCode: poRow.code || "",
        clientName: poRow.client_name || "",
        opsPriority: poRow.ops_priority || "medium",
        opsStatus: poRow.ops_status || "pending",
      });

      const { data: soRows, error: soErr } = await supabase
        .from("v_service_orders_enriched")
        .select("service_order_id, code, po_id, instrument_id, instrument_lab_id, instrument_tag, instrument_brand, instrument_model, instrument_serial, status, created_at")
        .eq("po_id", poId)
        .order("code", { ascending: true });
      if (soErr) throw soErr;

      const instrumentIds = (soRows || []).map((r) => r.instrument_id).filter(Boolean);
      const serviceOrderIds = (soRows || []).map((r) => r.service_order_id).filter(Boolean);

      let instrumentDetails = {};
      let admissionDates = {};
      let messageSummaries = {};
      if (instrumentIds.length > 0) {
        const { data: detailRows, error: detailErr } = await supabase
          .from("instruments")
          .select("id, status, type, unit, reception_date, technician_notes, rejection_reason, is_broken, repair_requested, lab_entry_date, reviewed_by, reviewed_at, category:category_id(name)")
          .in("id", instrumentIds);
        if (!detailErr && detailRows) {
          for (const row of detailRows) instrumentDetails[row.id] = row;
        }
      }

      if (serviceOrderIds.length > 0) {
        const { data: admissionRows, error: admissionError } = await supabase
          .from("service_orders")
          .select("id, reception_date")
          .in("id", serviceOrderIds);
        if (admissionError) throw admissionError;
        admissionDates = Object.fromEntries(
          (admissionRows || []).map((row) => [row.id, row.reception_date])
        );
        messageSummaries = await fetchServiceOrderMessageSummaries(serviceOrderIds, user?.id);
      }

      const merged = (soRows || []).map((r) => ({
        ...r,
        instrument_status:    resolveAdmissionStatus({
          instrumentStatus: instrumentDetails[r.instrument_id]?.status || "active",
          serviceOrderStatus: r.status,
          hasCurrentCycleTransition: hasCurrentAdmissionTransition({
            instrumentStatus: instrumentDetails[r.instrument_id]?.status,
            admissionStartedAt: r.created_at,
            reviewedAt: instrumentDetails[r.instrument_id]?.reviewed_at,
          }),
        }),
        instrument_type:      instrumentDetails[r.instrument_id]?.type || null,
        instrument_category_name: instrumentDetails[r.instrument_id]?.category?.name || null,
        instrument_unit:      instrumentDetails[r.instrument_id]?.unit || null,
        reception_date:       resolveAdmissionReceptionDate({
          serviceOrderReceptionDate: admissionDates[r.service_order_id],
          serviceOrderCreatedAt: r.created_at,
          legacyReceptionDate: instrumentDetails[r.instrument_id]?.reception_date,
        }),
        technician_notes:     instrumentDetails[r.instrument_id]?.technician_notes || "",
        rejection_reason:     instrumentDetails[r.instrument_id]?.rejection_reason || "",
        is_broken:            instrumentDetails[r.instrument_id]?.is_broken || false,
        repair_requested:     instrumentDetails[r.instrument_id]?.repair_requested ?? null,
        lab_entry_date:       instrumentDetails[r.instrument_id]?.lab_entry_date || null,
        reviewed_by:          instrumentDetails[r.instrument_id]?.reviewed_by || null,
        reviewed_at:          instrumentDetails[r.instrument_id]?.reviewed_at || null,
        message_summary:      messageSummaries[r.service_order_id] || { messageCount: 0, unreadCount: 0, lastMessageAt: null },
      }));

      setInstruments(merged);
    } catch (err) {
      console.error("[ServiceOrderInstruments] load error:", err);
      setErrorMsg(err?.message || "No se pudieron cargar los instrumentos.");
      setInstruments([]);
    } finally {
      setLoading(false);
    }
  }, [poId, user?.id]);

  useEffect(() => { load(); }, [load]);

  // Realtime
  useEffect(() => {
    if (!poId) return;
    const ch = supabase
      .channel(`service-order-${poId}-realtime`)
      .on("postgres_changes", { event: "update", schema: "public", table: "instruments" }, () => load())
      .on("postgres_changes", { event: "*",      schema: "public", table: "service_orders" }, () => load())
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "service_order_messages" }, () => load())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [poId, load]);

  async function handleUpdateStatus(instrumentId, newStatus, extraFields = {}) {
    try {
      const { error } = await supabase
        .from("instruments")
        .update({ status: newStatus, ...extraFields })
        .eq("id", instrumentId);
      if (error) throw error;

      setInstruments((prev) =>
        prev.map((i) =>
          i.instrument_id === instrumentId
            ? { ...i, instrument_status: newStatus, ...extraFields }
            : i
        )
      );
      setToast({ message: `Estado actualizado: ${STATUS_CONFIG[newStatus]?.label || newStatus}`, type: "success" });
    } catch (e) {
      console.error("[handleUpdateStatus]", e);
      setToast({ message: e.message || "No se pudo actualizar el estado.", type: "error" });
    }
  }

  // ─── acción masiva ────────────────────────────────────────────────────────

  function toggleSelect(instrumentId) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(instrumentId)) next.delete(instrumentId);
      else next.add(instrumentId);
      return next;
    });
  }

  function toggleSelectAll() {
    const selectable = instruments
      .filter((i) => !["remitted", "delivered"].includes(i.instrument_status || "active"))
      .map((i) => i.instrument_id);

    if (selectedIds.size === selectable.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectable));
    }
  }

  async function handleBulkApply(newStatus) {
    if (selectedIds.size === 0 || applying) return;

    const ids = [...selectedIds];
    const extraFields = newStatus === "reviewed"
      ? { reviewed_by: user?.id || null, reviewed_at: new Date().toISOString() }
      : {};

    setApplying(true);
    try {
      const { error } = await supabase
        .from("instruments")
        .update({ status: newStatus, ...extraFields })
        .in("id", ids);
      if (error) throw error;

      setInstruments((prev) =>
        prev.map((i) =>
          ids.includes(i.instrument_id)
            ? { ...i, instrument_status: newStatus, ...extraFields }
            : i
        )
      );
      setSelectedIds(new Set());
      setToast({
        message: `${ids.length} instrumento${ids.length !== 1 ? "s" : ""} marcados como "${STATUS_CONFIG[newStatus]?.label || newStatus}"`,
        type: "success",
      });
    } catch (e) {
      console.error("[handleBulkApply]", e);
      setToast({ message: e.message || "No se pudo aplicar el cambio masivo.", type: "error" });
    } finally {
      setApplying(false);
    }
  }

  // ─── render ───────────────────────────────────────────────────────────────

  const OS_STATUS_LABELS = {
    delivered_to_lab:                 "Entregado a laboratorio",
    calibration_finished:             "Calibración finalizada",
    certificates_finished_controlled: "Certificados revisados",
    uploaded_for_remito:              "Subido para remitar",
    remito_generated:                 "Remito generado",
    client_notified:                  "Cliente avisado",
    pending:                          "Pendiente",
    assigned:                         "Asignado",
  };

  const selectableCount = instruments.filter(
    (i) => !["remitted", "delivered"].includes(i.instrument_status || "active")
  ).length;
  const allSelected = selectableCount > 0 && selectedIds.size === selectableCount;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border/60 bg-background/90 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(returnTo)}
            className="px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted/60"
          >
            ← Volver
          </button>
          <div>
            <div className="font-semibold text-sm">{headerInfo?.poCode || "Orden de servicio"}</div>
            {headerInfo?.clientName && (
              <div className="text-xs text-muted-foreground">{headerInfo.clientName}</div>
            )}
          </div>
          {headerInfo?.opsStatus && (
            <span className="ml-auto text-xs text-white/40 border border-white/10 rounded-full px-2 py-0.5">
              {OS_STATUS_LABELS[headerInfo.opsStatus] || headerInfo.opsStatus}
            </span>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-3">
        {errorMsg && (
          <div className="rounded-md border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm">
            {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Cargando instrumentos…</div>
        ) : instruments.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">
            No hay instrumentos en esta orden de servicio.
          </div>
        ) : (
          <>
            {/* Resumen de estados */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-1">
              <StatusSummary instruments={instruments} />

              <div className="flex items-center gap-2 text-xs text-white/50">
                <span>{instruments.length} instrumento{instruments.length !== 1 ? "s" : ""}</span>
                {selectableCount > 0 && (
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="rounded border border-white/15 px-2 py-0.5 hover:border-sky-500/40 hover:text-sky-300 transition-colors"
                  >
                    {allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                  </button>
                )}
              </div>
            </div>

            {/* Barra de acción masiva — sticky cuando hay selección */}
            {someSelected && (
              <BulkActionBar
                count={selectedIds.size}
                role={p.role}
                onApply={handleBulkApply}
                onClear={() => setSelectedIds(new Set())}
                applying={applying}
              />
            )}

            {/* Botón "Listo para remitar" — jefe de lab */}
            {(p.isLabManager || p.isAdmin) && instruments.length > 0 && (() => {
              const allDone = instruments.every(i =>
                ["reviewed", "rejected", "ready_to_remit", "remitted", "delivered"].includes(i.instrument_status || "active")
              );
              const anyNotReady = instruments.some(i =>
                ["active", "in_progress", "calibrated", "with_certificate"].includes(i.instrument_status || "active")
              );
              if (!allDone || anyNotReady) return null;
              const alreadyReady = instruments.some(i =>
                ["ready_to_remit", "remitted", "delivered"].includes(i.instrument_status)
              );
              if (alreadyReady) return null;
              return (
                <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4 flex items-center justify-between gap-3">
                  <div className="text-sm text-sky-200">
                    Todos los instrumentos revisados. ¿Marcar la OS como lista para remitar?
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      for (const inst of instruments) {
                        if (inst.instrument_status === "reviewed") {
                          await handleUpdateStatus(inst.instrument_id, "ready_to_remit", {});
                        }
                      }
                    }}
                    className="shrink-0 rounded-lg border border-sky-500/50 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20"
                  >
                    📤 Listo para remitar
                  </button>
                </div>
              );
            })()}

            {/* Lista de instrumentos */}
            {instruments.map((inst) => (
              <InstrumentRow
                key={inst.service_order_id || inst.instrument_id}
                inst={inst}
                onUpdateStatus={handleUpdateStatus}
                role={p.role}
                userId={user?.id}
                selected={selectedIds.has(inst.instrument_id)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </>
        )}
      </main>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
